import type { AddProjectContentItemInputRequest } from '../types'
import type { ContentItemViewNodesType } from '../types/contentItemViewNode.type'
import { toPascalCase } from '../utils'
import { ObjectBuilder } from './ObjectBuilder'

/**
 * Abstract base class for building input variables for adding new project content items.
 * It encapsulates common logic and properties, requiring subclasses to implement specific behaviors.
 */
export abstract class CreateContentItemInputBuilder {
    protected text: string
    protected contentItem: ContentItemViewNodesType | undefined
    protected contentItemViewNodes: Partial<ContentItemViewNodesType>[]

    /**
     * Initializes the builder with the core data needed for adding an item.
     * @param text - The initial text content for the new item.
     * @param contentItemId - The sibling node id used for context (positioning, project details, etc.).
     * @param contentItemViewNodes - The list of all view nodes, needed for hierarchy traversal.
     */
    constructor(
        text: string,
        contentItemId: string,
        contentItemViewNodes: Partial<ContentItemViewNodesType>[] = []
    ) {
        this.text = text
        this.contentItemViewNodes = contentItemViewNodes
        this.contentItem = this.getContentItem(contentItemId)
    }

    /**
     * Retrieves the content item node by its id.
     * @param contentItemId - The id of the content item node to retrieve.
     * @returns The content item node with the given id, or undefined if not found.
     */
    protected getContentItem = (contentItemId: string) => {
        const foundArticle = this.contentItemViewNodes.find(
            (node) => node.id === contentItemId
        )

        return (foundArticle ||
            this.contentItemViewNodes.find((node) =>
                node.elements?.some((element) => element.id === contentItemId)
            )) as ContentItemViewNodesType
    }

    /**
     * Retrieves the parent type of the node from global store.
     * @param dtvParentId - The dtvItemId of the parent node.
     * @returns The parent's type converted to PascalCase.
     */
    protected getParentType = (dtvParentId: string): string => {
        let parentNode = this.contentItemViewNodes.find(
            (n) => n.dtvItemId === dtvParentId
        )

        if (!parentNode) {
            parentNode = this.contentItemViewNodes.find((node) =>
                node.elements?.some((element) => element.id === dtvParentId)
            )
        }

        return toPascalCase(parentNode?.type || 'None')
    }

    /**
     * Recursively finds the root Article node(s) for a given starting node.
     * @param node - The node to start the search from.
     * @returns An array containing the ID(s) of the root Article(s).
     */
    protected findRootArticleContentItem = (
        node: Partial<ContentItemViewNodesType> | undefined
    ): Partial<ContentItemViewNodesType> | null => {
        if (!node) {
            return null
        }

        if (node?.textType === 'Article') {
            return node
        }

        // Find the Article Content Item from its parent
        let parentNode = this.contentItemViewNodes.find(
            (ci) => ci.dtvItemId === node?.dtvParentId
        )

        // If dtvParentId is not found, try dtvContentItemParentId
        if (!parentNode) {
            parentNode = this.contentItemViewNodes.find(
                (ci) => ci.id === node.dtvContentItemParentId
            )
        }

        return this.findRootArticleContentItem(parentNode)
    }

    /**
     * Retrieves the root Article Content Item of a given node.
     * @param node - The node to start the search from.
     * @returns The root Article Item.
     */
    protected getRootArticle = (
        node: Partial<ContentItemViewNodesType> | undefined
    ): string[] => {
        const rootArticleId = this.findRootArticleContentItem(node)?.id
        const rootArticleContentItemIds: string[] = rootArticleId
            ? [rootArticleId]
            : []

        return rootArticleContentItemIds
    }

    /**
     * Builds the common parts of the nested 'input' object for the mutation.
     * @returns A partial representation of the 'input' object within AddProjectContentItemInputRequest.
     */
    protected buildBaseInput(): Partial<
        AddProjectContentItemInputRequest['input']
    > {
        return new ObjectBuilder<AddProjectContentItemInputRequest['input']>()
            .set('projectId', this.contentItem?.projectId)
            .set('projectProductFamilyId', this.contentItem?.productFamilyId)
            .set('elements', [
                {
                    text: this.text,
                },
            ])
            .set('textType', this.contentItem?.textType)
            .set('index', this.contentItem?.order || 0)
            .set('partType', this.contentItem?.partType)
            .set('productTypePropertyType', 'Shared')
            .build()
    }

    /**
     * Builds the common parts of the top-level request object for the mutation.
     * @returns A partial AddProjectContentItemInputRequest object with common properties set.
     */
    protected buildBaseRequest(): Partial<AddProjectContentItemInputRequest> {
        return new ObjectBuilder<AddProjectContentItemInputRequest>()
            .set('projectId', this.contentItem?.projectId)
            .set('partType', this.contentItem?.partType?.toUpperCase())
            .set('productFamilyId', this.contentItem?.productFamilyId)
            .set('isTrackChangeToggleOn', true)
            .set('rootArticleContentItemIds', [])
            .build()
    }

    /**
     * Abstract method to build the final mutation variables object.
     * Subclasses must implement this to combine base parts with type-specific details.
     * @returns The complete variables object (Partial<AddProjectContentItemInputRequest>).
     */
    abstract build(): Partial<AddProjectContentItemInputRequest>
}
